import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuButtonProps,
  useDisclosure,
} from "@chakra-ui/react";
import { ReactNode, useId, useRef, useState } from "react";

import { useAnimationFrame } from "@nens/react/hooks";

import { RanaButton } from "../../components/rana-button";
import { useKeyNavigation } from "../hooks/use-key-navigation";
import { MaxItemsState, useMaxItems } from "../hooks/use-max-items";

import { RanaSelectMenuList } from "./rana-select-menu-list";

// Do not allow undefined as a value being undefined means it should show the emptyLabel,
// while null can be a valid value that does not show the emptyLabel
export type RanaSelectValueType = string | number | null;

export interface RanaSelectProps<Value = RanaSelectValueType>
  extends Omit<MenuButtonProps, "onChange" | "value"> {
  options: {
    label: ReactNode | ((searchString: string) => ReactNode);
    value: Value;
    searchBy?: string;
  }[];
  onChange: (value: Value) => void;
  // If value undefined use emptyLabel
  value: Value | undefined;
  emptyLabel?: string;
  isReadOnly?: boolean;
  maxItemsState?: MaxItemsState;
}

export function RanaSelect<Value extends RanaSelectValueType>({
  options: unfilteredOptions,
  emptyLabel,
  onChange,
  isReadOnly,
  value,
  maxItemsState,
  ...rest
}: RanaSelectProps<Value>) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { maxItems, initialMaxItems, increaseMaxItems, resetMaxItems } = useMaxItems(maxItemsState);

  const [searchString, _setSearchString] = useState("");
  const setSearchString: typeof _setSearchString = (s) => {
    resetMaxItems();
    _setSearchString(s);
  };
  const inputRef = useRef<HTMLInputElement>(null);

  const valueLabel =
    unfilteredOptions.find((option) => value === option.value)?.label ?? emptyLabel;
  const id = useId();

  if (!isOpen && searchString) setSearchString("");

  const ensureFocus = () => {
    if (isOpen && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  };

  useAnimationFrame(ensureFocus, 6);

  const options = [
    ...(searchString
      ? unfilteredOptions.filter(
          ({ searchBy }) => !searchBy || searchBy.toUpperCase().includes(searchString.toUpperCase())
        )
      : unfilteredOptions),
  ];

  const { onKeyDown, selectedIdx } = useKeyNavigation({
    onChange,
    onClose,
    options,
    searchString,
    maxItems,
    increaseMaxItems,
  });

  return (
    <>
      <Input
        id={id}
        ref={inputRef}
        type="text"
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
        position="absolute"
        opacity={0}
        pointerEvents="none"
        height={0}
        width={0}
        onKeyDown={onKeyDown}
      />

      <Menu onClose={onClose} isOpen={isOpen} isLazy>
        <MenuButton
          bg="brand.grey.100"
          {...(isReadOnly ? { as: RanaButton, isDisabled: true } : { onClick: onOpen })}
          ref={buttonRef}
          value={value + ""}
          zIndex="base"
          {...rest}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Box>{typeof valueLabel === "function" ? valueLabel(searchString) : valueLabel}</Box>
            {!isReadOnly && <ChevronDownIcon />}
          </Flex>
        </MenuButton>
        <RanaSelectMenuList
          minW={`${buttonRef.current?.clientWidth ?? 0}px`}
          value={value}
          options={options}
          selectedIdx={selectedIdx}
          searchString={searchString}
          onChange={onChange}
          increaseMaxItems={increaseMaxItems}
          initialMaxItems={initialMaxItems}
          maxItems={maxItems}
        />
      </Menu>
    </>
  );
}
